import {useDndContext, useDroppable} from "@dnd-kit/core";
import clsx from "clsx";
import {Fragment, useCallback} from "react";
import {useTranslation} from "react-i18next";
import "./_async-table-group-dropzone.scss";
import {summarizeGroupRule} from "./asyncTableGroupRules";
import {MAX_GROUP_LEVELS} from "./asyncTableGrouping";

/**
 * Reguła poziomu na chipie: krótkie wyrażenie w całości, dłuższe jako liczba grup (całość w podpowiedzi).
 */
function GroupRuleSummary({rule, filterType}) {
    const {t} = useTranslation();
    const summary = summarizeGroupRule(rule, filterType);
    return (
        <span className="async-table-group-rule" title={rule.expression}>
            {summary.expression ??
                t(
                    summary.rest
                        ? "groupRuleGroupsCountWithRest"
                        : "groupRuleGroupsCount",
                    {count: summary.count}
                )}
        </span>
    );
}

function GroupChip({column, index, rule, onRemove, onConfigure}) {
    const {t} = useTranslation();

    const onRemoveClick = useCallback(
        () => onRemove(column.columnKey),
        [onRemove, column.columnKey]
    );

    const onConfigureClick = useCallback(
        () => onConfigure(column.columnKey),
        [onConfigure, column.columnKey]
    );

    const content = (
        <>
            <span className="async-table-group-dropzone-chip-name">
                {column.name}
            </span>
            {rule && (
                <GroupRuleSummary rule={rule} filterType={column.filterType} />
            )}
        </>
    );

    return (
        <span
            className={`async-table-group-dropzone-chip level-${index + 1}${index === 0 ? " first" : ""}`}>
            <span
                className="async-table-group-level"
                aria-label={t("groupLevel", {level: index + 1})}>
                {index + 1}
            </span>
            {onConfigure ? (
                <button
                    type="button"
                    className="async-table-group-dropzone-chip-configure"
                    aria-label={t("configureGroupingBy", {name: column.name})}
                    aria-haspopup="dialog"
                    onClick={onConfigureClick}>
                    {content}
                </button>
            ) : (
                content
            )}
            <button
                type="button"
                aria-label={t("removeGroupingBy", {name: column.name})}
                onClick={onRemoveClick}>
                <i className="fa-solid fa-xmark" />
            </button>
        </span>
    );
}

/**
 * Strefa "Grupuj według". Pusta pokazuje podpowiedź, a wrzucone kolumny jako numerowane chipy - kolejne poziomy.
 * Z `droppableId` strefa przyjmuje przeciągniętą kolumnę; nowa kolumna dochodzi jako kolejny poziom na końcu
 * (maks. MAX_GROUP_LEVELS). Z `onConfigure` kliknięcie chipa otwiera konfigurację poziomu (reguła grupowania),
 * a `rules` (po columnKey) pokazuje regułę poziomu na chipie.
 * @param {*} param0
 * @returns
 */
export default function AsyncTableGroupDropzone({
    columns = [],
    rules,
    hint,
    onRemove,
    onConfigure,
    className,
    droppableId,
}) {
    const {t} = useTranslation();

    const {setNodeRef, isOver} = useDroppable({
        id: droppableId ?? "async-table-group-dropzone-disabled",
        disabled: !droppableId,
    });

    const {active} = useDndContext();

    // przeciągana kolumna (z nagłówka tabeli albo z listy w ustawieniach)
    const dragged = droppableId ? active?.data.current?.column : null;

    const alreadyGrouped =
        !!dragged &&
        columns.some((column) => column.columnKey === dragged.columnKey);

    const full = columns.length >= MAX_GROUP_LEVELS;

    // nie da się upuścić: kolumna już grupuje albo limit poziomów
    const blocked = alreadyGrouped || full;

    const dragHint = !dragged
        ? null
        : alreadyGrouped
          ? t("alreadyGroupedBy", {name: dragged.name})
          : full
            ? t("maxGroupLevelsReached", {max: MAX_GROUP_LEVELS})
            : columns.length > 0
              ? t("dropToAddGroupingBy", {name: dragged.name})
              : t("dropToGroupBy", {name: dragged.name});

    // po wrzuceniu kolumn podpowiedź mówi, że można dodać kolejny poziom
    const idleHint =
        columns.length === 0
            ? hint
            : full
              ? null
              : t("dragHeaderToAddLevel", {max: MAX_GROUP_LEVELS});

    return (
        <div
            ref={setNodeRef}
            className={clsx("async-table-group-dropzone", className, {
                filled: columns.length > 0,
                active: !!dragged && !blocked,
                over: !!dragged && !blocked && isOver,
            })}>
            {columns.map((column, index) => (
                <Fragment key={column.columnKey}>
                    {index > 0 && (
                        <i
                            className="fa-solid fa-chevron-right async-table-group-dropzone-separator"
                            aria-hidden="true"
                        />
                    )}
                    <GroupChip
                        column={column}
                        index={index}
                        rule={rules?.[column.columnKey]}
                        onRemove={onRemove}
                        onConfigure={onConfigure}
                    />
                </Fragment>
            ))}
            {(dragHint ?? idleHint) && (
                <span className="async-table-group-dropzone-hint">
                    {dragHint ?? idleHint}
                </span>
            )}
        </div>
    );
}
